/**
 * FieldActions — shared Pencil + Clock icon group for all editable field rows.
 *
 * Design spec (DLD §B):
 * - 28px buttons, ≥44px touch target on mobile
 * - Reserved width so the layout does not shift on hover
 * - Hover/focus-reveal on desktop; always visible on touch devices
 * - role-gated: pencil only when canEdit; clock for any authenticated user
 * - Tooltip ("Edit" / "History") via components/ui/Tooltip
 */
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { Tooltip } from "./ui/Tooltip";

interface FieldActionsProps {
  /** Unique id for aria-label scoping. */
  fieldLabel: string;
  /** Show the Pencil button. Only true when the user canEdit. */
  canEdit: boolean;
  /** Show the Clock button. True for any authenticated user (role != null). */
  showClock: boolean;
  /** Callback when Pencil is clicked. */
  onEdit?: () => void;
  /** Custom render for the Clock trigger (used by FieldHistoryPopover). */
  clockTrigger?: React.ReactNode;
  /**
   * Optional ref forwarded to the pencil button. Used by DirectorsEditor /
   * OwnershipEditor to restore keyboard focus after the edit form closes.
   */
  editButtonRef?: React.Ref<HTMLButtonElement>;
  className?: string;
}

export function FieldActions({
  fieldLabel,
  canEdit,
  showClock,
  onEdit,
  clockTrigger,
  editButtonRef,
  className,
}: FieldActionsProps): JSX.Element {
  const { t } = useTranslation(["common", "audit"]);

  return (
    <span
      className={cn(
        // Reserved width so layout never shifts on hover
        "inline-flex items-center gap-0.5 shrink-0",
        // On desktop: reveal on group-hover/focus; on mobile: always tappable
        "opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity",
        className,
      )}
      aria-label={`${fieldLabel} actions`}
    >
      {canEdit && (
        <Tooltip content={t("common:edit")}>
          <button
            ref={editButtonRef}
            type="button"
            aria-label={t("common:edit_field", { field: fieldLabel, defaultValue: `Edit ${fieldLabel}` })}
            onClick={onEdit}
            className={cn(
              "inline-flex items-center justify-center rounded-md text-dim hover:text-accent",
              // 28px visual / ≥44px touch target
              "h-[28px] w-[28px] min-h-[44px] sm:min-h-[28px]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
            )}
          >
            <Pencil className="h-[13px] w-[13px]" aria-hidden />
          </button>
        </Tooltip>
      )}
      {showClock && clockTrigger}
    </span>
  );
}
